import { Checkbox } from '@liveauctioneers/hammer-ui-core/checkbox';
import { MagnifyingGlass } from '@liveauctioneers/hammer-ui-core/icons';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { BodySecondary, Caption } from '@liveauctioneers/hammer-ui-core/text';
import { TextInput } from '@liveauctioneers/hammer-ui-core/textInput';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { escapeRegExp } from 'es-toolkit';
import { Component, Fragment, ReactElement, ReactNode } from 'react';
import isEqual from 'react-fast-compare';

import { RouteLink } from '@/components/RouteLinks/RouteLinks';
import { ScrollBar } from '@/components/ScrollBar/ScrollBar';
import { SeeMoreItems } from '@/components/SeeMoreItems/SeeMoreItems';
import noop from '@/utils/noop';
import { modifyParamsRemovePage } from '@/utils/queryParams';
import { getCategoryFacetLink } from '@/utils/urls';

import { ExcludeFacetMenu } from './ExcludeFacetMenu';
import { Facet } from './Facet';
import { MobileExcludeConfirm } from './MobileExcludeConfirm';

const MAX_HEIGHT_FOR_CHECKS = 400;
const WEB_FACET_LIMIT = 6;
const MOBILE_FACET_LIMIT = 4;
const MAX_HEIGHT_FOR_SEE_MORE = 400;

type Props = {
    checklist: any[];
    emptyMessage: ReactNode;
    excludable?: boolean;
    facetId: string;
    facetOptionChecked: (facetName: string, value: string, isChecked: boolean) => void;
    forceEnabled: boolean;
    isMobileView?: boolean;
    loadPageWithFacetCollapsed?: boolean;
    queryParams: any;
    searchPlaceholder?: string;
    showAll: boolean;
    showCounts: boolean;
    showLandingPageLinks?: boolean;
    showSearchHeader: boolean;
    title: string;
    userIsLoggedIn?: boolean;
};

type State = {
    excludeHouseId: number | string;
    filteredList: any[];
    isHovering: boolean;
    searchText: string;
    showScrollBar: boolean;
};

export class MultiCheckFacet extends Component<Props, State> {
    static defaultProps = {
        excludable: false,
        forceEnabled: false,
        isMobileView: false,
        loadPageWithFacetCollapsed: false,
        showAll: false,
        showCounts: true,
        showLandingPageLinks: false,
    };

    state = {
        excludeHouseId: 0,
        filteredList: this.props.checklist,
        isHovering: false,
        searchText: '',
        showScrollBar: false,
    };

    shouldComponentUpdate = (nextProps: Props, nextState: State) =>
        !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);

    componentDidUpdate(prevProps: Props) {
        if (prevProps.checklist.length !== this.props.checklist.length) {
            this.setState({
                filteredList: this.props.checklist,
                searchText: '',
                showScrollBar: false,
            });
        } else if (this.state.searchText !== '') {
            const filteredList = this.filterList(this.state.searchText);
            this.setState({ filteredList });
        } else {
            this.setState({ filteredList: this.props.checklist });
        }
    }

    onMouseEnter = () => {
        this.setState({ isHovering: true });
    };

    onMouseLeave = () => {
        this.setState({ isHovering: false });
    };

    /**
     * showExcludeByHouseId reveals Auction House facet search exclusion CTA
     * @param houseId if > 0, reveals search houseExclusion; 0 hides CTA
     */
    showExcludeByHouseId = (houseId: number | string) => {
        this.setState({ excludeHouseId: houseId });
    };

    onConfirmExclusion = () => {
        // hides house exclusion foldout CTA
        this.showExcludeByHouseId(0);
    };

    onSeeMoreItemsClick = () => {
        const showScrollBar = !this.state.showScrollBar;
        this.setState({ showScrollBar });
    };

    filterList = (searchText: string) => {
        const search = new RegExp(`(?:^|\\W)${escapeRegExp(searchText)}`, 'i');
        return this.props.checklist.filter((checkListItem) => search.test(checkListItem.name));
    };

    filterListEvent = (event: any) => {
        const filteredList = this.filterList(event.target.value);
        this.setState({ filteredList, searchText: event.target.value });
    };

    check = (id: string, checked: boolean) => {
        this.props.facetOptionChecked(this.props.facetId, id, checked);
    };

    mapHyperLinkFilteredList = ({ count, excluded, id, landingPageUrl, name, selected }: any) => {
        let { queryParams } = this.props;
        queryParams = modifyParamsRemovePage(queryParams);
        const disabled = count === 0 || excluded;
        const showCounts = this.props.showCounts && !excluded;

        const resultsCountNode = showCounts && (
            <Caption
                className="ml-[4px]"
                suppressHydrationWarning
            >
                ({Number(count).toLocaleString()})
            </Caption>
        );

        const excludeFacetMenu = this.props.excludable && (
            <ExcludeFacetMenu
                excludeByHouseId={this.showExcludeByHouseId}
                isHovering={this.state.isHovering}
                isMobileView={this.props.isMobileView}
                optionId={id}
            />
        );

        return (
            <div
                className={cn('group', excluded)}
                key={`${name}-${id}-options`}
            >
                <div
                    className={cn(
                        // standard styles
                        'flex flex-nowrap justify-start px-xs py-2xs',
                        // mobile styles
                        'xsMax:py-sm',
                        // hover styles
                        'hover:cursor-pointer hover:bg-background-hover'
                    )}
                >
                    <RouteLink
                        className="flex h-full w-full flex-row flex-nowrap items-center justify-start no-underline"
                        to={getCategoryFacetLink(landingPageUrl, queryParams)}
                    >
                        <Checkbox.Option
                            checked={selected}
                            className="flex w-[25px] shrink-0 p-0"
                            disabled={disabled}
                            name={id}
                            onChange={noop}
                            onClick={(e) => {
                                e.stopPropagation();
                            }}
                        />
                        <div className="pointer-events-none ml-2xs mr-xs flex items-center overflow-hidden">
                            <BodySecondary>{name}</BodySecondary>
                            {resultsCountNode}
                        </div>
                    </RouteLink>
                </div>
                {excludeFacetMenu}
            </div>
        );
    };

    mapFilteredList = ({ count, excluded, id, name: initialName, selected, value }: any) => {
        let { forceEnabled, showCounts } = this.props;
        const disabled = (count === 0 || excluded) && !forceEnabled;
        // because its possible name could be "null" we'll add an extra check here to ensure that its a string
        const name = initialName === 'Pay Online' ? 'Pay On LiveAuctioneers' : initialName || '';

        const resultsCountNode = showCounts && (
            <Caption
                className="ml-[4px]"
                suppressHydrationWarning
            >
                ({Number(count).toLocaleString()})
            </Caption>
        );

        // expensive: only render and show if excludable is set

        let excludeFacetMenu: ReactElement;
        if (this.props.excludable && this.props.userIsLoggedIn) {
            const disabled = Boolean(this.state.excludeHouseId);
            excludeFacetMenu = this.props.isMobileView ? (
                <Link
                    className={cn({ 'text-text-tertiary': disabled })}
                    data-testid="mobile-link"
                    onClick={(event) => {
                        event.stopPropagation();
                        if (!disabled) {
                            this.showExcludeByHouseId(id);
                        }
                    }}
                >
                    <FormattedMessage id="search_facet_add_exclusion" />
                </Link>
            ) : (
                <ExcludeFacetMenu
                    data-testid="desktop-link"
                    excludeByHouseId={this.showExcludeByHouseId}
                    isHovering={this.state.isHovering}
                    isMobileView={this.props.isMobileView}
                    optionId={id}
                />
            );
        }

        // TODO: remove the `id || value` constructs, and fix the `auctionCalendar.js` module
        // to use the data structure patterns from `search.js` for consistency. (see user.js)
        const facetLineItem = (
            <div
                className="print:flex print:justify-between"
                key={`${name}-${id}-option`}
                onMouseEnter={this.onMouseEnter}
                onMouseLeave={this.onMouseLeave}
            >
                <div
                    className={cn(
                        '-ml-3xs flex items-start justify-start px-xs py-2xs',
                        // mobile styles
                        'xsMax:ml-0 xsMax:py-sm',
                        // hover
                        'hover:cursor-pointer hover:bg-background-hover'
                    )}
                    data-testid="facet-container"
                    onClick={() => {
                        this.check(id, !selected);
                    }}
                >
                    <Checkbox.Option
                        checked={selected}
                        className="flex w-[25px] shrink-0 p-0"
                        disabled={disabled}
                        name={id || value}
                        onChange={(e) => {
                            e.stopPropagation();
                            this.check(id, !selected);
                        }}
                    />
                    <div
                        className="pointer-events-none ml-2xs mr-xs flex items-center overflow-hidden"
                        data-testid={`${name?.replace(/\s/g, '')}FacetItem`}
                    >
                        <BodySecondary>{name}</BodySecondary>
                        {name === 'Top Rated' && (
                            <img
                                alt="badge"
                                className="mb-0 ml-xs mr-3xs mt-3xs h-[17px] w-[18px] align-middle"
                                src="https://p1.liveauctioneers.com/dist/images/badge.svg"
                            />
                        )}
                        {resultsCountNode}
                    </div>
                    {excludeFacetMenu}
                </div>
            </div>
        );

        if (this.props.isMobileView && this.state.excludeHouseId) {
            return this.state.excludeHouseId === id ? (
                <Fragment key={id}>
                    {facetLineItem}
                    <MobileExcludeConfirm
                        onConfirmExclusion={this.onConfirmExclusion}
                        optionId={id}
                        optionName={name}
                    />
                </Fragment>
            ) : undefined;
        } else {
            return facetLineItem;
        }
    };

    render() {
        const {
            emptyMessage,
            loadPageWithFacetCollapsed,
            searchPlaceholder,
            showLandingPageLinks,
            showSearchHeader,
            title = '',
        } = this.props;

        const { filteredList, showScrollBar } = this.state;
        let list: ReactNode[] = [];
        if (filteredList.length === 0) {
            list.push(emptyMessage);
        } else {
            list = filteredList.map((facetCheckbox) => {
                if (facetCheckbox.landingPageUrl && showLandingPageLinks) {
                    return this.mapHyperLinkFilteredList(facetCheckbox);
                }
                return this.mapFilteredList(facetCheckbox);
            });
        }
        list = list.filter((lineItem) => lineItem !== undefined);
        const placeholderText = searchPlaceholder || `Search ${title}s`;
        const search = showSearchHeader && (
            <div className="relative mb-md pl-xs xsMax:mb-lg">
                <TextInput
                    aria-label={placeholderText}
                    id={placeholderText}
                    onChange={this.filterListEvent}
                    placeholder={placeholderText}
                    value={this.state.searchText}
                />
                <MagnifyingGlass
                    className="pointer-events-none absolute right-xs top-1/2 -translate-y-1/2 cursor-pointer text-icon-primary hover:text-link-hover"
                    size="sm"
                />
            </div>
        );

        const maxHeight = showScrollBar ? MAX_HEIGHT_FOR_SEE_MORE : MAX_HEIGHT_FOR_CHECKS;
        const facetClass = title.trim().replace('/', '').replace(/\s+/g, '-').toLowerCase();
        const shortListSliceCount = this.props.isMobileView ? MOBILE_FACET_LIMIT : WEB_FACET_LIMIT;

        return (
            <Facet
                className={`multicheck-facet ${facetClass}`}
                closeFacet={loadPageWithFacetCollapsed}
                header={search}
                testid={facetClass}
                title={title}
            >
                <ScrollBar
                    className="relative flex flex-nowrap justify-start"
                    maxHeight={maxHeight}
                >
                    <SeeMoreItems
                        hideSeeLess
                        itemsComponentFull={list}
                        itemsComponentShort={list.slice(0, shortListSliceCount)}
                        itemsListLengthFull={list.length}
                        itemsListLengthShort={list.slice(0, shortListSliceCount).length}
                        onSeeMoreItemsClick={this.onSeeMoreItemsClick}
                        showAllItems={this.props.showAll}
                    />
                </ScrollBar>
            </Facet>
        );
    }
}
